import { type URLOpenListenerEvent } from '@capacitor/app';
import {
  APP_AUTH_APP_LINK_PATH,
  APP_AUTH_CALLBACK_HOST,
  APP_AUTH_CALLBACK_SCHEME,
  APP_AUTH_CUSTOM_SCHEME_PATH,
} from '@/shared/libs/auth/app-auth';

export type AppAuthProvider = 'google' | 'telegram';
export type AppAuthType = 'signin' | 'signup';

const APP_AUTH_INTERNAL_PARAMS = new Set([
  'provider',
  'authType',
  'nextUrl',
  'sessionId',
]);

const isCustomSchemeCallback = (url: URL): boolean =>
  url.protocol === `${APP_AUTH_CALLBACK_SCHEME}:` &&
  url.hostname === APP_AUTH_CALLBACK_HOST &&
  url.pathname === APP_AUTH_CUSTOM_SCHEME_PATH;

const isHttpsAppLinkCallback = (url: URL): boolean =>
  url.protocol === 'https:' && url.pathname === APP_AUTH_APP_LINK_PATH;

export const isAppAuthCallback = (url: URL): boolean =>
  isCustomSchemeCallback(url) || isHttpsAppLinkCallback(url);

const isAppAuthProvider = (value: string | null): value is AppAuthProvider =>
  value === 'google' || value === 'telegram';

const normalizeAuthType = (value: string | null): AppAuthType =>
  value === 'signup' ? 'signup' : 'signin';

export type ParsedAppAuthCallback = {
  url: URL;
  provider: AppAuthProvider | null;
  authType: AppAuthType;
  nextUrl: string;
  sessionId: string | null;
  credential: string | null;
  code: string | null;
  tgQuery: string;
};

const parseGoogleState = (
  rawState: string | null
): Pick<ParsedAppAuthCallback, 'provider' | 'authType' | 'nextUrl'> | null => {
  if (!rawState) {
    return null;
  }

  try {
    const parsedState = JSON.parse(atob(rawState)) as {
      provider?: string;
      authType?: string;
      nextUrl?: string;
    };

    return {
      provider: isAppAuthProvider(parsedState.provider || null)
        ? parsedState.provider
        : null,
      authType: normalizeAuthType(parsedState.authType || null),
      nextUrl: parsedState.nextUrl || '/',
    };
  } catch {
    return null;
  }
};

export const parseAppAuthCallback = (
  event: Pick<URLOpenListenerEvent, 'url'>
): ParsedAppAuthCallback | null => {
  const callbackUrl = new URL(event.url);

  if (!isAppAuthCallback(callbackUrl)) {
    return null;
  }

  const providerValue = callbackUrl.searchParams.get('provider');
  const parsedGoogleState = parseGoogleState(callbackUrl.searchParams.get('state'));
  const provider = parsedGoogleState?.provider ||
    (isAppAuthProvider(providerValue) ? providerValue : null);
  const nextUrl = parsedGoogleState?.nextUrl ||
    callbackUrl.searchParams.get('nextUrl') ||
    '/';
  const tgParams = new URLSearchParams(callbackUrl.searchParams);

  APP_AUTH_INTERNAL_PARAMS.forEach((param) => {
    tgParams.delete(param);
  });
  tgParams.delete('state');

  return {
    url: callbackUrl,
    provider,
    authType:
      parsedGoogleState?.authType ||
      normalizeAuthType(callbackUrl.searchParams.get('authType')),
    nextUrl,
    sessionId: callbackUrl.searchParams.get('sessionId'),
    credential: callbackUrl.searchParams.get('credential'),
    code: callbackUrl.searchParams.get('code'),
    tgQuery: tgParams.toString(),
  };
};
